/**
 * SECTION 08 — Risk Distribution & Assessment Statistics
 *
 * Single-source: Reviewer Console → Executive Summary (Live)
 *   • Assessment Statistics  (reviewer_get_progress_summary)
 *   • Risk Distribution      (derived from the same row)
 *   • Cyber Resilience band  (passed from generator — same band used in
 *                              Executive Summary and Section 03)
 *
 * No legacy/derived recalculations.
 */
import type jsPDF from "jspdf";
import { PAGE_MARGIN, REPORT_THEME, type RGB } from "./theme";
import { drawRunningHeader, drawPageFooter, drawSectionTitle } from "./pageChrome";
import { type MaturityBand } from "@/reports/telecom/cyberResilienceSummary";
import { drawCyberResilienceCard } from "./cyberResilienceCard";

export interface RiskDistributionMetrics {
  totalControls: number;
  implemented: number;
  partial: number;
  notImplemented: number;
  totalFindings: number;
  highFindings: number;
  moderateFindings: number;
  lowFindings: number;
}

export interface RiskDistributionContext {
  metrics: RiskDistributionMetrics | null;
  clientOrgName: string;
  methodology?: string | null;
  frameworks?: string[] | null;
  sectionNumber?: string;
  /** Same overall band shown in Executive Summary + Section 03. */
  resilienceBand?: MaturityBand | "Unrated" | null;
}

const GREEN: RGB = [22, 163, 74];
const AMBER: RGB = [245, 158, 11];
const RED: RGB   = [220, 38, 38];
const GRAY: RGB  = [148, 163, 184]; // slate-400 — matches Roadmap "Low"
const NAVY       = REPORT_THEME.navy.rgb as RGB;

interface Segment { label: string; value: number; color: RGB }

function drawArc(
  doc: jsPDF, cx: number, cy: number, r: number,
  startDeg: number, endDeg: number, color: RGB, strokeW: number,
): void {
  doc.setDrawColor(color[0], color[1], color[2]);
  doc.setLineWidth(strokeW);
  doc.setLineCap("butt");
  const steps = Math.max(8, Math.ceil(Math.abs(endDeg - startDeg) / 4));
  const toRad = (d: number) => (d * Math.PI) / 180;
  let prevX = cx + r * Math.cos(toRad(startDeg));
  let prevY = cy + r * Math.sin(toRad(startDeg));
  for (let i = 1; i <= steps; i++) {
    const t = i / steps;
    const a = startDeg + (endDeg - startDeg) * t;
    const x = cx + r * Math.cos(toRad(a));
    const y = cy + r * Math.sin(toRad(a));
    doc.line(prevX, prevY, x, y);
    prevX = x; prevY = y;
  }
  doc.setLineWidth(0.5);
}

function drawDonut(
  doc: jsPDF, cx: number, cy: number, rOuter: number, thickness: number,
  segments: Segment[],
): void {
  const total = segments.reduce((s, x) => s + Math.max(0, x.value), 0);
  if (total <= 0) {
    drawArc(doc, cx, cy, rOuter - thickness / 2, 0, 360, [225, 230, 238], thickness);
    return;
  }
  let startDeg = -90;
  for (const seg of segments) {
    if (seg.value <= 0) continue;
    const sweep = (seg.value / total) * 360;
    drawArc(doc, cx, cy, rOuter - thickness / 2, startDeg, startDeg + sweep, seg.color, thickness);
    startDeg += sweep;
  }
}

function pctNum(value: number, total: number): number {
  if (!total || total <= 0) return 0;
  return Math.round((value / total) * 100);
}
function pct(value: number, total: number): string { return `${pctNum(value, total)}%`; }

function dominant(segments: Segment[]): { seg: Segment; pct: number; total: number } | null {
  const total = segments.reduce((s, x) => s + Math.max(0, x.value), 0);
  if (total <= 0) return null;
  const visible = segments.filter((s) => s.value > 0);
  if (!visible.length) return null;
  const top = [...visible].sort((a, b) => b.value - a.value)[0];
  return { seg: top, pct: pctNum(top.value, total), total };
}

/** Summary metric card — neutral KPI styling (no color accent). */
function drawCard(
  doc: jsPDF, x: number, y: number, w: number, h: number,
  _accent: RGB, value: string | number, label: string,
): void {
  doc.setFillColor(255, 255, 255);
  doc.setDrawColor(...REPORT_THEME.border.rgb);
  doc.setLineWidth(0.6);
  doc.roundedRect(x, y, w, h, 4, 4, "FD");

  doc.setFont("helvetica", "bold");
  doc.setFontSize(typeof value === "string" && value.length > 4 ? 14 : 20);
  doc.setTextColor(...REPORT_THEME.navy.rgb);
  doc.text(String(value), x + 10, y + Math.min(h - 18, 28));

  doc.setFont("helvetica", "semibold" as any);
  doc.setFontSize(7);
  doc.setTextColor(...REPORT_THEME.textMuted.rgb);
  doc.text(label.toUpperCase(), x + 10, y + h - 8);
}


/** Donut block with dominant-category center label + legend (zeros suppressed). */
function drawDonutBlock(
  doc: jsPDF, x: number, y: number, w: number, h: number,
  title: string, centerLabelSuffix: string, segments: Segment[],
): void {
  doc.setFillColor(255, 255, 255);
  doc.setDrawColor(...REPORT_THEME.border.rgb);
  doc.setLineWidth(0.6);
  doc.roundedRect(x, y, w, h, 6, 6, "FD");

  doc.setFont("helvetica", "bold");
  doc.setFontSize(10.5);
  doc.setTextColor(...REPORT_THEME.navy.rgb);
  doc.text(title, x + 14, y + 22);
  doc.setDrawColor(...REPORT_THEME.gold.rgb);
  doc.setLineWidth(1);
  doc.line(x + 14, y + 27, x + 14 + 40, y + 27);

  const visible = segments.filter((s) => s.value > 0);
  const total = segments.reduce((s, seg) => s + Math.max(0, seg.value), 0);

  // Donut area
  const donutAreaW = w * 0.46;
  const cx = x + donutAreaW / 2 + 6;
  const cy = y + h / 2 + 12;
  const rOuter = Math.min(donutAreaW, h - 60) / 2 - 4;
  const thickness = Math.max(14, rOuter * 0.32);
  drawDonut(doc, cx, cy, rOuter, thickness, segments);

  // Center — dominant category (constrained inside inner circle)
  const innerR = Math.max(10, rOuter - thickness - 2);
  // Safe horizontal text width inside the inner circle (chord with margin)
  const safeWidth = innerR * 1.7;

  const top = dominant(segments);
  if (top) {
    // Percentage — scale down if needed to fit safeWidth
    doc.setFont("helvetica", "bold");
    let pctSize = 20;
    doc.setFontSize(pctSize);
    const pctStr = `${top.pct}%`;
    while (doc.getTextWidth(pctStr) > safeWidth && pctSize > 12) {
      pctSize -= 1;
      doc.setFontSize(pctSize);
    }
    doc.setTextColor(...top.seg.color);

    // Label — try sizes 8.5 → 6.5; wrap up to 2 lines that each fit safeWidth
    const labelText = `${top.seg.label} ${centerLabelSuffix}`.trim();
    doc.setFont("helvetica", "bold");
    let labelSize = 8.5;
    let labelLines: string[] = [];
    while (labelSize >= 6.5) {
      doc.setFontSize(labelSize);
      const wrapped: string[] = doc.splitTextToSize(labelText, safeWidth);
      const fits = wrapped.slice(0, 2).every((ln) => doc.getTextWidth(ln) <= safeWidth);
      if (fits && wrapped.length <= 2) {
        labelLines = wrapped;
        break;
      }
      labelLines = wrapped.slice(0, 2);
      if (fits) break;
      labelSize -= 0.5;
    }
    const lineH = labelSize + 1.5;

    // Vertically center the (percentage + label lines) stack inside inner circle
    const pctH = pctSize * 0.72;
    const totalH = pctH + 4 + labelLines.length * lineH;
    const topY = cy - totalH / 2 + pctH;

    doc.setFontSize(pctSize);
    doc.setTextColor(...top.seg.color);
    doc.text(pctStr, cx, topY, { align: "center" });

    doc.setFontSize(labelSize);
    doc.setTextColor(...REPORT_THEME.navy.rgb);
    let ly = topY + 4 + labelSize;
    for (const ln of labelLines) {
      doc.text(ln, cx, ly, { align: "center" });
      ly += lineH;
    }
  } else {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.setTextColor(...REPORT_THEME.textMuted.rgb);
    doc.text("No data", cx, cy + 4, { align: "center" });
  }

  // Legend — suppress zero values, add breathing room
  const legendX = x + donutAreaW + 18;
  let ly = y + 52;
  const rowH = 26;
  for (const seg of visible) {
    doc.setFillColor(...seg.color);
    doc.roundedRect(legendX, ly - 8, 10, 10, 1.5, 1.5, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(...REPORT_THEME.navy.rgb);
    doc.text(seg.label, legendX + 16, ly);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    doc.setTextColor(...REPORT_THEME.textPrimary.rgb);
    doc.text(`${seg.value}  ·  ${pct(seg.value, total)}`, legendX + 16, ly + 12);
    ly += rowH;
    if (ly > y + h - 14) break;
  }

  // Footer-of-card: total count, subtle
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7.5);
  doc.setTextColor(...REPORT_THEME.textMuted.rgb);
  doc.text(`Total ${total}`, x + w - 14, y + h - 10, { align: "right" });
}

/** Soft callout card (light bg, gold left accent). */
function drawInsightCallout(
  doc: jsPDF, x: number, y: number, w: number,
  title: string, body: string,
): number {
  const pad = 12;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  const bodyLines = doc.splitTextToSize(body, w - pad * 2 - 6);
  const h = 22 + bodyLines.length * 13 + pad;

  // Background
  doc.setFillColor(250, 247, 238); // soft cream/gold tint
  doc.setDrawColor(...REPORT_THEME.border.rgb);
  doc.setLineWidth(0.6);
  doc.roundedRect(x, y, w, h, 5, 5, "FD");
  // Left accent
  doc.setFillColor(...REPORT_THEME.gold.rgb);
  doc.rect(x, y, 4, h, "F");

  doc.setFont("helvetica", "bold");
  doc.setFontSize(10.5);
  doc.setTextColor(...REPORT_THEME.navy.rgb);
  doc.text(title, x + pad + 4, y + 18);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(...REPORT_THEME.textPrimary.rgb);
  doc.text(bodyLines, x + pad + 4, y + 34, { lineHeightFactor: 1.45 } as any);

  return y + h;
}

function buildKeyInsight(m: RiskDistributionMetrics): string {
  const outcomes: Segment[] = [
    { label: "Fully Implemented",     value: m.implemented,    color: GREEN },
    { label: "Partially Implemented", value: m.partial,        color: AMBER },
    { label: "Not Implemented",       value: m.notImplemented, color: RED   },
  ];
  const risks: Segment[] = [
    { label: "High",     value: m.highFindings,     color: RED   },
    { label: "Moderate", value: m.moderateFindings, color: AMBER },
    { label: "Low",      value: m.lowFindings,      color: GRAY  },
  ];
  const oTop = dominant(outcomes);
  const rTop = dominant(risks);

  // Executive-style sentence 1 — capability posture
  let s1: string;
  if (!oTop) {
    s1 = "The assessment did not generate sufficient control results to characterise capability posture.";
  } else if (oTop.seg.label === "Fully Implemented") {
    s1 = "The assessment indicates that core cybersecurity capabilities are operating consistently across the business, with most controls performing as intended.";
  } else if (oTop.seg.label === "Partially Implemented") {
    s1 = "The assessment indicates that foundational cybersecurity capabilities are in place, but most controls require further maturation and consistency.";
  } else {
    s1 = "The assessment indicates material capability gaps across the cybersecurity program, with most controls not yet operating to an acceptable standard.";
  }

  // Executive-style sentence 2 — risk concentration
  let s2: string;
  if (!rTop) {
    s2 = "";
  } else if (rTop.seg.label === "High") {
    s2 = "Risk exposure is concentrated within High findings, signalling focused weaknesses that warrant priority remediation and executive attention.";
  } else if (rTop.seg.label === "Moderate") {
    s2 = "Risk exposure is concentrated within Moderate findings, suggesting broad improvement opportunities rather than isolated critical weaknesses.";
  } else {
    s2 = "Residual risk is weighted toward Low findings, indicating exposure is largely tactical and can be addressed through routine control hygiene.";
  }

  return s2 ? `${s1} ${s2}` : s1;
}


function buildCommentary(m: RiskDistributionMetrics, org: string): string {
  const total = m.totalControls;
  if (total <= 0) {
    return `${org}'s assessment has not yet generated control results. Statistics and risk distribution will populate once the reviewer completes scoring.`;
  }
  const outcomes: Segment[] = [
    { label: "Fully Implemented",     value: m.implemented,    color: GREEN },
    { label: "Partially Implemented", value: m.partial,        color: AMBER },
    { label: "Not Implemented",       value: m.notImplemented, color: RED   },
  ];
  const oTop = dominant(outcomes);
  const dominantPhrase = oTop
    ? oTop.seg.label === "Fully Implemented"
      ? "most controls were assessed as Fully Implemented, indicating consistent capability across domains"
      : oTop.seg.label === "Partially Implemented"
        ? "most controls were assessed as Partially Implemented, indicating that control effectiveness varies across domains"
        : "most controls were assessed as Not Implemented, indicating material capability gaps across the program"
    : "the control population is spread across implementation tiers";

  const p1 =
    `The assessment evaluated ${m.totalControls} control${m.totalControls === 1 ? "" : "s"} and identified ` +
    `${m.totalFindings} finding${m.totalFindings === 1 ? "" : "s"} across ${org}'s cybersecurity program. ` +
    `While foundational security practices are present, ${dominantPhrase}.`;

  const risks: Segment[] = [
    { label: "High",     value: m.highFindings,     color: RED   },
    { label: "Moderate", value: m.moderateFindings, color: AMBER },
    { label: "Low",      value: m.lowFindings,      color: GRAY  },
  ];
  const rTop = dominant(risks);
  let p2: string;
  if (!rTop) {
    p2 = `No reviewer findings have been recorded at this stage, so the residual risk profile cannot yet be characterised.`;
  } else if (rTop.seg.label === "High") {
    p2 = `Residual risk is primarily concentrated within the High category. This signals focused exposure that should anchor the immediate remediation roadmap and executive attention.`;
  } else if (rTop.seg.label === "Moderate") {
    p2 = `Residual risk is primarily concentrated within the Moderate category. This suggests ${org} faces broad but manageable cybersecurity challenges that can be addressed through targeted remediation and governance improvements.`;
  } else {
    p2 = `Residual risk skews toward the Low category, indicating exposure is largely tactical. Closure should be planned as part of business-as-usual control hygiene.`;
  }
  return `${p1}\n\n${p2}`;
}

export function renderRiskDistribution(
  doc: jsPDF,
  ctx: RiskDistributionContext,
): number {
  const m = ctx.metrics;
  if (!m || m.totalControls <= 0) {
    return doc.getNumberOfPages();
  }

  doc.addPage();
  const firstPage = doc.getNumberOfPages();
  const pageW = doc.internal.pageSize.getWidth();
  const innerW = pageW - PAGE_MARGIN * 2;
  const sectionNumber = ctx.sectionNumber ?? "08";
  const sectionTitle = "Risk Distribution & Assessment Statistics";

  drawRunningHeader(doc, {
    clientName: ctx.clientOrgName || "Client",
    frameworks: ctx.frameworks ?? null,
    methodology: ctx.methodology ?? null,
  });
  let y = drawSectionTitle(doc, { sectionNumber, title: sectionTitle, continued: false });

  // Intro
  const intro =
    "This section summarizes assessment outcomes and the distribution of identified risks. The statistics and visualizations below provide an overview of control implementation maturity and the concentration of findings across risk levels.";
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10.5);
  doc.setTextColor(...REPORT_THEME.textPrimary.rgb);
  const introLines = doc.splitTextToSize(intro, innerW);
  doc.text(introLines, PAGE_MARGIN, y, { lineHeightFactor: 1.5 } as any);
  y += introLines.length * 14 + 14;

  // ── Summary cards: 3 rows × 3 (last row holds Cyber Resilience) ──
  const cardGap = 10;
  const cardCols = 3;
  const cardW = (innerW - cardGap * (cardCols - 1)) / cardCols;
  const cardH = 54; // tightened ~13% from 62

  const resilience = ctx.resilienceBand ?? "Unrated";

  const row1: { label: string; value: string | number; accent: RGB }[] = [
    { label: "Controls Assessed",     value: m.totalControls,  accent: REPORT_THEME.accent.rgb as RGB },
    { label: "Fully Implemented",     value: m.implemented,    accent: GREEN },
    { label: "Partially Implemented", value: m.partial,        accent: AMBER },
  ];
  const row2: { label: string; value: string | number; accent: RGB }[] = [
    { label: "Not Implemented",       value: m.notImplemented, accent: RED },
    { label: "Total Findings",        value: m.totalFindings,  accent: NAVY },
    { label: "High Findings",         value: m.highFindings,   accent: RED },
  ];
  const row3Plain: { label: string; value: string | number; accent: RGB }[] = [
    { label: "Moderate Findings",     value: m.moderateFindings, accent: AMBER },
    { label: "Low Findings",          value: m.lowFindings,      accent: GREEN },
  ];

  for (const row of [row1, row2]) {
    row.forEach((c, i) => {
      const x = PAGE_MARGIN + i * (cardW + cardGap);
      drawCard(doc, x, y, cardW, cardH, c.accent, c.value, c.label);
    });
    y += cardH + cardGap;
  }
  // Row 3: two regular cards + Cyber Resilience tinted card (shared helper)
  row3Plain.forEach((c, i) => {
    const x = PAGE_MARGIN + i * (cardW + cardGap);
    drawCard(doc, x, y, cardW, cardH, c.accent, c.value, c.label);
  });
  const resilienceX = PAGE_MARGIN + 2 * (cardW + cardGap);
  drawCyberResilienceCard(doc, resilienceX, y, cardW, cardH, resilience);
  y += cardH + cardGap + 6;

  // ── Two donut visualisations ──
  const blockGap = 14;
  const blockW = (innerW - blockGap) / 2;
  const blockH = 180;
  drawDonutBlock(doc, PAGE_MARGIN, y, blockW, blockH, "Assessment Results", "", [
    { label: "Fully Implemented",     value: m.implemented,    color: GREEN },
    { label: "Partially Implemented", value: m.partial,        color: AMBER },
    { label: "Not Implemented",       value: m.notImplemented, color: RED   },
  ]);
  drawDonutBlock(doc, PAGE_MARGIN + blockW + blockGap, y, blockW, blockH, "Risk Distribution", "Risk", [
    { label: "High",     value: m.highFindings,     color: RED   },
    { label: "Moderate", value: m.moderateFindings, color: AMBER },
    { label: "Low",      value: m.lowFindings,      color: GRAY  },
  ]);
  y += blockH + 10;

  // ── Footnote under charts ──
  doc.setFont("helvetica", "italic");
  doc.setFontSize(8.5);
  doc.setTextColor(...REPORT_THEME.textMuted.rgb);
  doc.text(
    "Risk distribution reflects finalized findings generated during the assessment process.",
    PAGE_MARGIN,
    y,
  );
  y += 16;

  // ── Key Insight callout ──
  const insight = buildKeyInsight(m);
  if (insight.trim().length > 0) {
    y = drawInsightCallout(doc, PAGE_MARGIN, y, innerW, "Key Insight", insight) + 18;
  }


  // ── Executive Commentary ──
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(...REPORT_THEME.navy.rgb);
  doc.text("Executive Commentary", PAGE_MARGIN, y);
  doc.setDrawColor(...REPORT_THEME.gold.rgb);
  doc.setLineWidth(1);
  doc.line(PAGE_MARGIN, y + 5, PAGE_MARGIN + 64, y + 5);
  y += 16;

  const commentary = buildCommentary(m, ctx.clientOrgName || "the organization");
  const paragraphs = commentary.split(/\n\n+/);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(...REPORT_THEME.textPrimary.rgb);
  for (const para of paragraphs) {
    const lines = doc.splitTextToSize(para, innerW);
    doc.text(lines, PAGE_MARGIN, y, { lineHeightFactor: 1.5 } as any);
    y += lines.length * 13 + 8;
  }

  const totalPages = doc.getNumberOfPages();
  for (let p = firstPage; p <= totalPages; p++) {
    doc.setPage(p);
    drawPageFooter(doc, p);
  }
  return totalPages;
}
